import React, { useState, useEffect } from 'react';
import { FaPaperPlane, FaEnvelope, FaLinkedin } from 'react-icons/fa';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import Turnstile from 'react-turnstile';

const Contact = () => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        message: ''
    });
    const [captchaToken, setCaptchaToken] = useState(null);

    // Character limit constant
    const MAX_CHARS = 3000;

    // Handle Safari bfcache (back-forward cache) to prevent stale captcha tokens
    useEffect(() => {
        const handlePageShow = (event) => {
            if (event.persisted) {
                setCaptchaToken(null);
            }
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!captchaToken) {
            toast.error("Please complete the CAPTCHA check.");
            return;
        }

        setLoading(true);

        try {
            const { error } = await supabase
                .from('contact_messages')
                .insert([
                    {
                        first_name: formData.firstName,
                        last_name: formData.lastName,
                        email: formData.email,
                        message: formData.message
                    }
                ]);

            if (error) throw error;

            toast.success('Message sent successfully!');
            setFormData({ firstName: '', lastName: '', email: '', message: '' });
            setCaptchaToken(null); // Reset captcha
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='w-full py-24 px-4 bg-white dark:bg-[#0a1220] transition-colors duration-300'>
            <div className='max-w-[1240px] mx-auto grid lg:grid-cols-2 gap-16'>

                {/* Contact Info */}
                <div className='flex flex-col justify-center text-gray-800 dark:text-gray-300'>
                    <h1 className='text-4xl md:text-5xl font-bold bg-gradient-to-r from-secondary to-accent inline-block text-transparent bg-clip-text mb-6'>
                        Let's Talk
                    </h1>
                    <p className='text-xl mb-12 text-gray-600 dark:text-gray-400 leading-relaxed'>
                        Have questions about our SQL courses? Enterprise training needs? Or just want to say hi? We'd love to hear from you.
                    </p>

                    <div className='space-y-8'>
                        <div className='flex items-center gap-6'>
                            <div className='w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center text-secondary'>
                                <FaEnvelope size={20} />
                            </div>
                            <div>
                                <h3 className='font-bold text-gray-900 dark:text-white text-lg'>Email Us</h3>
                                <p className='text-gray-500'>vikrant@datahelix.io</p>
                            </div>
                        </div>

                        <div className='flex items-center gap-6'>
                            <div className='w-12 h-12 rounded-full bg-sky-600/10 flex items-center justify-center text-sky-600'>
                                <FaLinkedin size={20} />
                            </div>
                            <div>
                                <h3 className='font-bold text-gray-900 dark:text-white text-lg'>Connect on LinkedIn</h3>
                                <a href="https://www.linkedin.com/in/vikrantkumar95/" target="_blank" rel="noopener noreferrer" className='text-gray-500 hover:text-secondary transition-colors'>
                                    View Profile
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Form */}
                <div className='bg-gray-50 dark:bg-[#162032] p-8 md:p-12 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800'>
                    <form className='flex flex-col gap-6' onSubmit={handleSubmit}>
                        <div className='grid md:grid-cols-2 gap-6'>
                            <div className='flex flex-col gap-2'>
                                <label className='font-semibold text-gray-700 dark:text-gray-300 text-sm'>First Name</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    placeholder="Enter First Name"
                                    required
                                    className='p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-secondary transition-colors'
                                />
                            </div>
                            <div className='flex flex-col gap-2'>
                                <label className='font-semibold text-gray-700 dark:text-gray-300 text-sm'>Last Name</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    placeholder="Enter Last Name"
                                    required
                                    className='p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-secondary transition-colors'
                                />
                            </div>
                        </div>

                        <div className='flex flex-col gap-2'>
                            <label className='font-semibold text-gray-700 dark:text-gray-300 text-sm'>Email</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter Email Address"
                                required
                                className='p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-secondary transition-colors'
                            />
                        </div>

                        <div className='flex flex-col gap-2'>
                            <div className="flex justify-between items-end">
                                <label className='font-semibold text-gray-700 dark:text-gray-300 text-sm'>Message</label>
                                <span className={`text-xs ${formData.message.length >= MAX_CHARS ? 'text-red-500' : 'text-gray-400'}`}>
                                    {formData.message.length}/{MAX_CHARS}
                                </span>
                            </div>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                rows={5}
                                maxLength={MAX_CHARS}
                                placeholder="Enter your message..."
                                required
                                className='p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-secondary transition-colors resize-none'
                            />
                        </div>

                        {/* Turnstile Captcha */}
                        <div className="flex justify-center">
                            <Turnstile
                                sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000AA"}
                                onVerify={(token) => setCaptchaToken(token)}
                                onError={() => toast.error("CAPTCHA failed. Please try again.")}
                                onExpire={() => setCaptchaToken(null)}
                                theme="dark"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className='bg-secondary text-white font-bold py-4 rounded-xl shadow-lg shadow-secondary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 mt-2 group disabled:opacity-70 disabled:cursor-not-allowed'
                        >
                            {loading ? 'Sending...' : 'Send Message'}
                            {!loading && <FaPaperPlane className='group-hover:translate-x-1 transition-transform' />}
                        </button>
                    </form>
                </div>

            </div>
        </div>
    );
};

export default Contact;
